import { useState } from 'react';
import { Swords } from 'lucide-react';
import { OrgCard } from '@/features/OrgLayout/primitives/OrgCard';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useOrgHackathons, useOrgHackathonLeaderboard } from '@/lib/hooks/useHackathons';
import type { Hackathon, HackathonStatus } from '@/lib/api/hackathons';

// Read-only surface: org admins get visibility into a batch's hackathons — the
// design doc grants them "status + leaderboard" visibility, but no management
// rights. Deliberately renders no create / edit / grade affordances and no
// mutations. The only interactive control is a per-card leaderboard disclosure
// toggle, which reveals live standings without navigating away. Trainers own the
// hackathon lifecycle (see the trainer tab).

// Map each lifecycle status to a shadcn Badge variant. `live` is the active,
// attention-worthy state → the primary (`default`) variant; the rest are quieter.
const STATUS_VARIANT: Record<HackathonStatus, 'default' | 'secondary' | 'outline'> = {
  draft: 'secondary',
  scheduled: 'outline',
  live: 'default',
  completed: 'secondary',
};

function formatWindow(startAt: string, endAt: string): string {
  const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' };
  const start = new Date(startAt);
  const end = new Date(endAt);
  return `${start.toLocaleString(undefined, opts)} → ${end.toLocaleString(undefined, opts)}`;
}

export function BatchHackathonsTab({ batchId }: { batchId: string }) {
  const { data: hackathons, isLoading } = useOrgHackathons(batchId);

  if (isLoading) {
    return <div className="p-6 text-sm text-[#6b7280]">Loading hackathons…</div>;
  }

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-semibold text-[#111827]">Hackathons</h2>
        <p className="text-sm text-[#6b7280]">Competitions trainers have set up for this batch.</p>
      </div>

      {!hackathons?.length ? (
        <OrgCard className="flex flex-col items-center gap-2 p-10 text-center">
          <span className="flex h-[42px] w-[42px] items-center justify-center rounded-[14px] bg-[#fff4f0]">
            <Swords className="h-5 w-5 text-[#ff5a1f]" />
          </span>
          <p className="text-sm text-[#6b7280]">No hackathons for this batch yet.</p>
        </OrgCard>
      ) : (
        <ul className="grid gap-3">
          {hackathons.map((h) => (
            <li key={h.id}>
              <HackathonRow batchId={batchId} hackathon={h} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// One card per hackathon: status pill always visible, leaderboard revealed on
// demand. Keeping the disclosure state per-row means each card owns its own
// expand/collapse and the leaderboard query only fires once its panel mounts.
function HackathonRow({ batchId, hackathon }: { batchId: string; hackathon: Hackathon }) {
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const panelId = `hackathon-leaderboard-${hackathon.id}`;

  return (
    <OrgCard className="p-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="font-medium text-[#111827]">{hackathon.title}</p>
          <p className="mt-0.5 text-xs text-[#6b7280]">{formatWindow(hackathon.start_at, hackathon.end_at)}</p>
        </div>
        <Badge
          variant={STATUS_VARIANT[hackathon.status] ?? STATUS_VARIANT.draft}
          className="flex-shrink-0 capitalize"
        >
          {hackathon.status}
        </Badge>
      </div>

      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => setShowLeaderboard((v) => !v)}
        aria-expanded={showLeaderboard}
        aria-controls={panelId}
        className="mt-3 h-auto px-2 py-1 text-xs font-medium text-[#ff5a1f] hover:bg-[#fff4f0] hover:text-[#ff5a1f]"
      >
        {showLeaderboard ? 'Hide leaderboard' : 'Show leaderboard'}
      </Button>

      {showLeaderboard && (
        <div id={panelId}>
          <LeaderboardPanel batchId={batchId} hackathonId={hackathon.id} />
        </div>
      )}
    </OrgCard>
  );
}

// Live standings, read-only. Reuses the same simple rank/team/points table markup
// as the trainer detail view — no grading, no mutations.
function LeaderboardPanel({ batchId, hackathonId }: { batchId: string; hackathonId: string }) {
  const { data: leaderboard, isLoading } = useOrgHackathonLeaderboard(batchId, hackathonId);

  if (isLoading) {
    return <p className="mt-3 border-t border-[#ececec] pt-3 text-sm text-[#6b7280]">Loading leaderboard…</p>;
  }

  return (
    <div className="mt-3 border-t border-[#ececec] pt-3">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="h-auto py-1">Rank</TableHead>
            <TableHead className="h-auto py-1">Team</TableHead>
            <TableHead className="h-auto py-1 text-right">Points</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {leaderboard?.map((entry) => (
            <TableRow key={entry.team_id}>
              <TableCell className="py-1">#{entry.rank}</TableCell>
              <TableCell className="py-1">{entry.team_name}</TableCell>
              <TableCell className="py-1 text-right">{entry.score}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {!leaderboard?.length && (
        <p className="py-4 text-center text-sm text-[#6b7280]">No scores yet.</p>
      )}
    </div>
  );
}
